import type { ReactNode } from 'react';
import { chromaticWavesVideo } from '@shared/assets';
import { AuthLogoLink, AuthSupportFooter } from '@shared/ui';
import { LocaleThemeControls } from '@features/change-language';
import { useTranslation } from 'react-i18next';

interface AuthScreenProps {
  children: ReactNode;
}

export function AuthScreen({ children }: AuthScreenProps) {
  const { t } = useTranslation('auth');

  return (
    <div className="flex min-h-screen flex-col lg:flex-row">
      <div
        className="relative flex h-[140px] shrink-0 flex-col justify-end overflow-hidden px-6 pb-8 lg:hidden"
        style={{
          borderRadius: '0 0 var(--radius-3xl, 24px) var(--radius-3xl, 24px)',
          background: 'linear-gradient(194deg, var(--base-primary, #0C0A09) 18.76%, #262626 95.17%)',
        }}
      >
        <video
          autoPlay
          loop
          muted
          playsInline
          className="pointer-events-none absolute inset-0 z-0 h-full w-full object-cover opacity-80"
        >
          <source src={chromaticWavesVideo} type="video/mp4" />
        </video>
        <div className="relative z-10">
          <AuthLogoLink className="h-7" />
        </div>
      </div>

      <div
        className="relative hidden flex-shrink-0 flex-col justify-between overflow-hidden p-12 lg:flex lg:w-[596px]"
        style={{
          borderRadius: '0 var(--radius-4xl, 32px) var(--radius-4xl, 32px) 0',
          background: 'linear-gradient(194deg, var(--base-primary, #0C0A09) 18.76%, #262626 95.17%)',
        }}
      >
        <video
          autoPlay
          loop
          muted
          playsInline
          className="pointer-events-none absolute inset-0 z-0 h-full w-full object-cover"
        >
          <source src={chromaticWavesVideo} type="video/mp4" />
        </video>
        <div className="absolute inset-0 z-0 opacity-10">
          <div className="absolute left-20 top-20 h-96 w-96 rounded-full bg-white blur-3xl" />
          <div className="absolute bottom-20 right-20 h-96 w-96 rounded-full bg-cyan-300 blur-3xl" />
        </div>
        <div className="relative z-10">
          <AuthLogoLink className="h-10" />
        </div>
        <div className="relative z-10 space-y-8">
          <div>
            <h1
              className="mb-4 text-primary-foreground"
              style={{ fontFamily: 'Sora, sans-serif', fontSize: '48px', fontWeight: 800, lineHeight: '120%' }}
            >
              {t('marketing.acceptCryptoPayments').replace('Crypto ', 'Crypto\n')}
            </h1>
            <p
              className="text-primary-foreground"
              style={{ fontFamily: 'Sora, sans-serif', fontSize: '20px', fontWeight: 600, lineHeight: '28px' }}
            >
              {t('marketing.mostPowerfulGateway')}
            </p>
          </div>
        </div>
        <div className="relative z-10">
          <p className="text-sm text-primary-foreground">
            {t('marketing.joinBusinesses')}
          </p>
        </div>
      </div>

      <div className="flex flex-1 flex-col bg-background">
        <div className="flex flex-1 items-center justify-center p-6 lg:p-8">
          <div className="relative flex w-full max-w-[360px] flex-col items-center gap-3">
            <LocaleThemeControls className="absolute right-0 top-0.5 z-10" />
            {children}
          </div>
        </div>
        <AuthSupportFooter />
      </div>
    </div>
  );
}
